import { callBackendCommand, firebaseFunctionCommands, getBackend } from '@/backend';
import type { AuthSession } from '@/backend/contracts';
import { logger } from '@/utils/logger';
import type { UserProfile } from './types';
import {
    authCurrentUser,
    publishCurrentUserProfile,
    setCurrentAuthUidProvider,
} from './current-user-identity';

export { authCurrentUser } from './current-user-identity';

function identityClient() {
    const identity = getBackend().identity;
    if (!identity) throw new Error('Identity client is unavailable');
    return identity;
}

function sessionToUserProfile(session: AuthSession): UserProfile {
    return {
        uid: session.userId,
        authUid: session.authUid,
        canonicalUserId: session.canonicalUserId,
        authEmail: session.authEmail ?? session.email,
        email: session.email,
        displayName: session.displayName,
        photoURL: session.photoURL ?? undefined,
        isSuperAdmin: session.isSuperAdmin,
        activeOrgId: session.activeOrgId ?? undefined,
        needsSetup: session.needsSetup,
        projectWelcomeDismissals: session.projectWelcomeDismissals,
    };
}

setCurrentAuthUidProvider(() => identityClient().getAuthUid());

function publishSession(session: AuthSession | null): UserProfile | null {
    return publishCurrentUserProfile(session ? sessionToUserProfile(session) : null);
}

export function seedE2EAuthProfileForTesting(user: Pick<UserProfile, 'uid' | 'email' | 'displayName'> & Partial<UserProfile>): void {
    if (import.meta.env.VITE_E2E !== 'true') return;

    publishCurrentUserProfile({
        uid: user.uid,
        authUid: user.authUid ?? user.uid,
        canonicalUserId: user.canonicalUserId ?? user.uid,
        authEmail: user.authEmail ?? user.email,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        isSuperAdmin: user.isSuperAdmin,
        activeOrgId: user.activeOrgId,
        needsSetup: user.needsSetup,
        calendarFeedTokens: user.calendarFeedTokens,
        calendarTokens: user.calendarTokens,
        projectWelcomeDismissals: user.projectWelcomeDismissals,
    });
}

export function getCurrentUser() {
    return authCurrentUser;
}

export function getCurrentFirebaseAuthUid(): string | undefined {
    return identityClient().getAuthUid();
}

export async function loginWithGoogle() {
    return publishSession(await identityClient().signInWithGoogle());
}

export async function loginWithEmail(email: string, password: string) {
    return publishSession(await identityClient().signInWithEmail(email, password));
}

export async function signupWithEmail(email: string, password: string, displayName: string) {
    return publishSession(await identityClient().signUpWithEmail(email, password, displayName));
}

export async function refreshCurrentAuthToken(): Promise<void> {
    await identityClient().getToken({ forceRefresh: true });
}

export async function refreshCurrentAuthProfile(): Promise<UserProfile | null> {
    return publishSession(await identityClient().refreshSession());
}

export async function linkGoogleProviderToCurrentAuth() {
    await identityClient().linkGoogleProvider();
}

export async function signInWithGoogleProviderForAuthorizationLink() {
    return publishSession(await identityClient().signInWithGoogleForAuthorizationLink());
}

export async function logout() {
    await identityClient().signOut();
    publishSession(null);
}

export function initAuth(onUserChange: (user: UserProfile | null) => void): () => void {
    return identityClient().subscribe((session) => {
        const profile = publishSession(session);
        onUserChange(profile);
    });
}

export async function generateCalendarToken(userId: string, orgId: string, projectId: string): Promise<string> {
    try {
        const result = await callBackendCommand(firebaseFunctionCommands.generateCalendarFeedToken, {
            userId,
            orgId,
            projectId,
        });
        return result.token;
    } catch (error: unknown) {
        logger.error('Failed to generate calendar token:', error);
        throw error;
    }
}

export async function getCalendarToken(userId: string, projectId: string): Promise<string | null> {
    try {
        const result = await callBackendCommand(firebaseFunctionCommands.getCalendarFeedToken, {
            userId,
            projectId,
        });
        return result.token;
    } catch (error: unknown) {
        logger.error('Failed to get calendar token:', error);
        throw error;
    }
}

export async function regenerateCalendarToken(userId: string, orgId: string, projectId: string): Promise<string> {
    try {
        const result = await callBackendCommand(firebaseFunctionCommands.regenerateCalendarFeedToken, {
            userId,
            orgId,
            projectId,
        });
        return result.token;
    } catch (error: unknown) {
        logger.error('Failed to regenerate calendar token:', error);
        throw error;
    }
}
